import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { usePwaInstall } from "../contexts/PwaInstallContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Lock, Download } from "lucide-react";
import { toast, Toaster } from "sonner";
import GoogleSignInButton from "../components/GoogleSignInButton";
import IosInstallModal from "../components/IosInstallModal";

const API = window.location.origin;
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function LoginPage() {
  const { login, loginWithGoogleCustom } = useAuth();
  const navigate = useNavigate();
  // Dopo il login, se è presente un ?redirect=/path sicuro (es. pagina di
  // consenso OAuth del connettore MCP), vai lì invece che alla home.
  const goAfterLogin = () => {
    const r = new URLSearchParams(window.location.search).get("redirect");
    if (r && r.startsWith("/") && !r.startsWith("//")) {
      window.location.href = r;
    } else {
      navigate("/");
    }
  };
  const { canInstall, promptInstall, isIOSSafari, installLabel } = usePwaInstall();
  const [iosModalOpen, setIosModalOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isForgot, setIsForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);

  // Notifica esplicita quando l'utente viene rediretto qui per inattività:
  // il monitor di idle (IdleLogoutMonitor) appende `?reason=inactivity`.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("reason") === "inactivity") {
      toast.info("Sei stato disconnesso per inattività. Effettua di nuovo l'accesso.");
      // Pulisci il query param per evitare di rimostrare il toast in caso di refresh
      const url = window.location.pathname;
      window.history.replaceState({}, "", url);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Inserisci email e password"); return; }
    setIsLoading(true);
    try {
      await login(email, password);
      goAfterLogin();
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 404 || status === 400) {
        toast.error("Utente non valido");
      } else {
        toast.error(err.response?.data?.detail || "Errore di autenticazione");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Callback chiamata dal componente GoogleSignInButton quando l'utente
  // completa il flow Google e riceviamo l'ID token JWT. Lo inviamo al
  // backend per verifica + creazione sessione.
  const handleGoogleCredential = async (credential) => {
    setIsLoading(true);
    try {
      await loginWithGoogleCustom(credential);
      goAfterLogin();
    } catch (err) {
      const msg = err.response?.data?.detail || "Errore di autenticazione Google";
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) { toast.error("Inserisci la tua email"); return; }
    setIsLoading(true);
    try {
      const axios = (await import("axios")).default;
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
      toast.success("Email inviata! Controlla la tua casella.");
    } catch {
      toast.error("Errore nell'invio dell'email");
    } finally {
      setIsLoading(false);
    }
  };

  if (isForgot) {
    return (
      <div className="min-h-screen bg-[#f5f7fa] bg-grid-light flex items-center justify-center px-4">
        <Toaster position="top-right" theme="light" />
        <div className="w-full max-w-md">
          <div className="bg-[#1a1fa8] rounded-3xl px-8 py-10 pb-16 relative">
            <h1 className="font-mono-menu text-2xl font-semibold text-white mb-1">Recupera Password</h1>
            <p className="text-white/70 text-sm">Inserisci la tua email per ricevere il link di reset</p>
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-[#1a1fa8]" />
          </div>
          <div className="bg-white rounded-3xl p-8 shadow-md -mt-8 relative z-10">
            {forgotSent ? (
              <div className="text-center py-4" data-testid="forgot-success">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-green-600" />
                </div>
                <p className="text-slate-700 font-medium mb-2">Email inviata!</p>
                <p className="text-slate-500 text-sm mb-6">Controlla la tua casella di posta per il link di reset.</p>
                <button onClick={() => { setIsForgot(false); setForgotSent(false); }} className="text-[#3B42DD] font-medium text-sm hover:underline">
                  Torna al login
                </button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-5">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="La tua email" className="pl-10 h-12 bg-[#f8f9fb] border-slate-200 rounded-xl" data-testid="forgot-email-input" />
                  </div>
                </div>
                <Button type="submit" disabled={isLoading} className="w-full h-12 bg-[#1a1fa8] hover:bg-[#f8dfd6] hover:text-[#1a1fa8] text-white rounded-xl font-semibold transition-colors" data-testid="forgot-submit">
                  {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Invia link di reset"}
                </Button>
                <button type="button" onClick={() => setIsForgot(false)} className="w-full text-center text-sm text-[#3B42DD] font-medium hover:underline">
                  Torna al login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f5f7fa] bg-grid-light flex items-center justify-center px-4">
      <Toaster position="top-right" theme="light" />
      <div className="w-full max-w-md">
        <div className="bg-[#1a1fa8] rounded-3xl px-8 py-10 pb-16 relative">
          <h1 className="font-mono-menu text-2xl font-semibold text-white mb-1">BDD Studio Dentistico</h1>
          <p className="text-white/70 text-sm">Accedi al tuo account</p>
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-[#1a1fa8]" />
        </div>
        <div className="bg-white rounded-3xl p-8 shadow-md -mt-8 relative z-10 space-y-6">
          {/* Google Login (Custom OAuth — bottone GSI ufficiale). */}
          <div data-testid="google-login-btn">
            <GoogleSignInButton
              onCredential={handleGoogleCredential}
              onError={(e) => toast.error(e?.message || "Errore Google Sign-In")}
              disabled={isLoading}
            />
          </div>

          {/* Separator */}
          <div className="flex items-center gap-3">
            <div className="flex-1 border-t border-slate-200" />
            <span className="text-xs text-slate-400 font-medium">oppure</span>
            <div className="flex-1 border-t border-slate-200" />
          </div>

          {/* Email/Password Login.
              `autoComplete="off"` da solo non basta (Chrome/Edge ignorano
              su input type=password): aggiungiamo anche `name` randomizzati
              ad ogni mount e `readOnly` rimosso al focus per evitare che il
              browser memorizzi/autocompili le credenziali. */}
          <form onSubmit={handleLogin} className="space-y-4" autoComplete="off" data-form-type="other">
            {/* Trappola anti-autofill: input "esca" che cattura l'eventuale
                autocomplete del browser senza disturbare l'utente. */}
            <input type="text" name="fake-user" autoComplete="username" style={{ display: "none" }} aria-hidden="true" />
            <input type="password" name="fake-pass" autoComplete="new-password" style={{ display: "none" }} aria-hidden="true" />
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="email"
                  name={`login_email_${Math.random().toString(36).slice(2, 8)}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  className="pl-10 h-12 bg-[#f8f9fb] border-slate-200 rounded-xl"
                  data-testid="login-email-input"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  type="password"
                  name={`login_pwd_${Math.random().toString(36).slice(2, 8)}`}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="La tua password"
                  className="pl-10 pr-3 h-12 bg-[#f8f9fb] border-slate-200 rounded-xl"
                  data-testid="login-password-input"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                />
              </div>
            </div>
            <div className="text-right">
              <button type="button" onClick={() => setIsForgot(true)} className="text-xs text-[#3B42DD] font-medium hover:underline" data-testid="forgot-password-link">
                Password dimenticata?
              </button>
            </div>
            <Button type="submit" disabled={isLoading} className="w-full h-12 bg-[#1a1fa8] hover:bg-[#f8dfd6] hover:text-[#1a1fa8] text-white rounded-xl font-semibold transition-colors" data-testid="login-submit-btn">
              {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Accedi"}
            </Button>
          </form>
          {canInstall && (
            <button
              type="button"
              onClick={() => (isIOSSafari ? setIosModalOpen(true) : promptInstall())}
              data-testid="install-app-btn"
              className="w-full flex items-center justify-center gap-2 h-11 text-sm font-medium text-[#1a1fa8] border border-[#1a1fa8]/20 rounded-xl hover:bg-[#1a1fa8]/5 transition-colors"
            >
              <Download className="w-4 h-4" />
              {installLabel}
            </button>
          )}
        </div>
      </div>
      <IosInstallModal open={iosModalOpen} onClose={() => setIosModalOpen(false)} />
    </div>
  );
}
